import React, { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrthographicCamera } from '@react-three/drei';
import * as THREE from 'three';
import AnimatedCircuitSequence from './AnimatedCircuitSequence'; // 新しいアニメーションコンポーネントをインポート
//import CircuitLines from './CircuitLines'; // 後で作成
//import ElectricParticles from './ElectricParticles'; // 後で作成

// 回路データの型定義 (例)
export interface CircuitNode {
  id: string;
  position: THREE.Vector3;
  connections: string[]; // 接続先ノードのIDの配列
}
export interface CircuitData {
  nodes: CircuitNode[];
}

// 簡単な回路データを生成する関数 (例)
const generateCircuitData = (width: number, height: number, numNodes: number): CircuitData => {
  const nodes: CircuitNode[] = [];
  const gridSize = Math.ceil(Math.sqrt(numNodes));
  const cellWidth = width / gridSize;
  const cellHeight = height / gridSize;

  for (let i = 0; i < numNodes; i++) {
    const gridX = i % gridSize;
    const gridY = Math.floor(i / gridSize);
    nodes.push({
      id: `node-${i}`,
      // 2D平面上に配置 (Z=0)
      position: new THREE.Vector3(
        (gridX - gridSize / 2 + 0.5) * cellWidth + (Math.random() - 0.5) * cellWidth * 0.5,
        (gridY - gridSize / 2 + 0.5) * cellHeight + (Math.random() - 0.5) * cellHeight * 0.5,
        0
      ),
      connections: [],
    });
  }

  // ランダムに接続 (簡易版)
  nodes.forEach((node, i) => {
    for (let j = i + 1; j < nodes.length; j++) {
      if (Math.random() < 0.9) { // 10%の確率で接続
        const dist = node.position.distanceTo(nodes[j].position);
        if (dist < Math.min(width, height) / (gridSize / 2)) { // ある程度近いノード同士
          node.connections.push(nodes[j].id);
          nodes[j].connections.push(node.id);
        }
      }
    }
  });
  return { nodes };
};


const CircuitBackground: React.FC = () => {
  const frustumSize = 100;
  const aspect = typeof window !== "undefined" ? window.innerWidth / window.innerHeight : 1;

  const circuitData = useMemo(() => generateCircuitData(frustumSize * aspect, frustumSize, 5), [aspect, frustumSize]);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: -1, background: 'linear-gradient(to bottom, #000010, #001028)' }}>
      <Canvas
        style={{ width: '50vw', height: '100vh', position: 'absolute', top: '0', left: '50vw', zIndex: -1 }}
      >
        <OrthographicCamera
          makeDefault
          left={frustumSize * aspect / -2}
          right={frustumSize * aspect / 2}
          top={frustumSize / 2}
          bottom={frustumSize / -2}
          near={0.1}
          far={1000}
          position={[0, 0, 100]}
        />
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 50]} intensity={0.2} />

        {/* AnimatedCircuitSequence のみ呼び出す */}
        {circuitData.nodes.length > 0 && (
            <AnimatedCircuitSequence
                circuitData={circuitData}
                pathChangeInterval={6000} // 6秒ごとにパス変更 (例)
                particleCount={6}       // パーティクル数を調整 (例)
                fadeDuration={1000}      // フェードイン・アウトの時間 (例)
            />
        )}
      </Canvas>
    </div>
  );
};

export default CircuitBackground;